import { useQuery, useQueryClient } from 'react-query';
import { useToast } from '@chakra-ui/react';

import { useAsync } from '../../../../lib/client/hooks';
import * as services from '../services';
import {
  PENDING_COUNT_KEY,
  PENDING_REFETCH_MS,
  REQUESTS_KEY,
  TOAST_DURATION_MS,
} from '../constants';

const DECIDE_FALLBACK_MESSAGE = 'Gagal menyimpan keputusan';
const SUCCESS_TITLE = {
  approved: 'Izin disetujui',
  rejected: 'Izin ditolak',
};

/**
 * Daftar pengajuan. `data` = body server utuh (Ruling 9), jadi baris dibaca
 * sebagai `data?.data?.docs`. Data lama tetap tampil saat filter berganti.
 */
export function useStudentLeaveRequests(params) {
  return useQuery(
    [REQUESTS_KEY, params],
    async () => {
      const response = await services.getStudentLeaveRequests(params);
      return response.data;
    },
    { keepPreviousData: true },
  );
}

/**
 * Jumlah pengajuan yang menunggu. Dipakai Task 5 (badge menu) sebagai
 * `data?.data?.pending`; nama dan bentuk kembalian ini adalah kontrak.
 * `enabled: false` mematikan query (dan polling-nya) untuk role yang ditolak
 * rute ini, misalnya finance (403).
 *
 * @param {{ enabled?: boolean }} [options]
 */
export function useStudentLeavePendingCount({ enabled = true } = {}) {
  return useQuery(
    [PENDING_COUNT_KEY],
    async () => {
      const response = await services.getStudentLeavePendingCount();
      return response.data;
    },
    {
      enabled,
      refetchInterval: PENDING_REFETCH_MS,
      refetchOnWindowFocus: true,
    },
  );
}

const decideRequest = ({ id, payload }) =>
  services.decideStudentLeaveRequest(id, payload);

/**
 * Mengirim keputusan, menampilkan toast, dan menginvalidasi daftar serta
 * angka pending. Galat dilempar ulang supaya modal bisa memutuskan apakah
 * ia ditutup.
 */
export function useDecideStudentLeave() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { execute, status } = useAsync(decideRequest);

  async function decide(id, payload) {
    try {
      const body = await execute({ id, payload });
      toast({
        title: SUCCESS_TITLE[payload.status],
        status: 'success',
        duration: TOAST_DURATION_MS,
      });
      queryClient.invalidateQueries(REQUESTS_KEY);
      queryClient.invalidateQueries(PENDING_COUNT_KEY);
      return body;
    } catch (error) {
      toast({
        title: error?.response?.data?.message || DECIDE_FALLBACK_MESSAGE,
        status: 'error',
        duration: TOAST_DURATION_MS,
      });
      // 409/404 berarti data di layar basi; muat ulang keduanya.
      queryClient.invalidateQueries(REQUESTS_KEY);
      queryClient.invalidateQueries(PENDING_COUNT_KEY);
      throw error;
    }
  }

  return { decide, isSubmitting: status === 'fetching' };
}
